import React, { useCallback } from "react";
import { RoutineEditNavigator } from "./RoutineEditMain/RoutineEditNavigation";
import { RoutineEditChecklist } from "./RoutineEditChecklist/RoutineEditChecklist";
import { RoutineEditOwnerTeam } from "./RoutineEditOwnerTeam";
import { RoutineEditChecklistSectionOrderTasks } from "./RoutineEditChecklist/RoutineEditChecklistSection/RoutineEditChecklistSectionOrderTasks";
import { RoutineEditChecklistTask } from "./RoutineEditChecklistTask";
import { RoutineEditChecklistOrderSections } from "./RoutineEditChecklist/RoutineEditChecklistOrderSections";
import { RoutineEditChecklistSectionIssueTeam } from "./RoutineEditChecklist/RoutineEditChecklistSection/RoutineEditChecklistSectionIssueTeam";
import { RoutineEditFileSelect, RoutineEditAllFiles } from "../FileSelect";

import { RoutineEditMain } from "./RoutineEditMain/RoutineEditMain";
import { RoutineEditScheduleDailyTaskImportance } from "./RoutineEditScheduleDailyTaskImportance/RoutineEditScheduleDailyTaskImportance";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import RoutineEditChecklistSectionAddExistingTasks from "./RoutineEditChecklist/RoutineEditChecklistSection/RoutineEditChecklistSectionAddExistingTasks";
import { useNavigationHeaderOptions } from "../../common/navigationHeaderOptions";
import { RoutineEditStateProvider } from "./State/RoutineEditState";
import { graphql, useLazyLoadQuery } from "react-relay";
import { RoutineEditQuery } from "./__generated__/RoutineEditQuery.graphql";
import { UserLoggedInNavigatorRouteProp } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WorkspaceRoutineEditFileSelect } from "./RoutineEditMain/WorkspaceRoutineEditFileSelect";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { WorkspaceRoutineEditSectionSettings } from "./RoutineEditMain/WorkspaceRoutineEditSectionSettings";

export const RoutineEdit = withWebModal(RoutineEditC);

function RoutineEditC(props: {
  route: UserLoggedInNavigatorRouteProp<"RoutineEdit">;
}) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerRoutineEditC {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerRoutineEditC(props: {
  route: UserLoggedInNavigatorRouteProp<"RoutineEdit">;
  fetchKey: string;
}) {
  const getOptions = useNavigationHeaderOptions();
  const data = useLazyLoadQuery<RoutineEditQuery>(
    graphql`
      query RoutineEditQuery(
        $id: ID!
        $workspaceId: ID!
        $includeWorkspace: Boolean!
      ) {
        routine(id: $id) @required(action: THROW) {
          ...RoutineEditStateProvider_routine
          ...RoutineEditMainRoutine_Routine
        }
        workspace(id: $workspaceId) @include(if: $includeWorkspace) {
          topics {
            ...RoutineEditMainOrgTopicListItem_topics
          }
        }
      }
    `,
    {
      id: props.route.params.id,
      workspaceId: props.route.params.orgWorkspaceId ?? "",
      includeWorkspace: !!props.route.params.orgWorkspaceId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const renderRoutineEditMain = useCallback(
    (p: { route: any; navigation: any }) => (
      <RoutineEditMain
        {...p}
        routine={data.routine}
        topics={data.workspace?.topics ?? null}
      />
    ),
    [data.routine, data.workspace?.topics]
  );

  return (
    <RoutineEditStateProvider
      routineKey={data.routine}
      params={props.route.params}
      key={props.route.params.id}
    >
      <RoutineEditNavigator.Navigator
        screenOptions={getOptions({ type: "DEFAULT" })}
      >
        <RoutineEditNavigator.Screen
          name="RoutineEditMain"
          initialParams={{
            ...props.route.params,
          }}
          options={({ navigation }) =>
            getOptions({
              type: "SAVE-INIT",
              onCancel: () => {
                navigation.goBack();
              },
            })
          }
        >
          {renderRoutineEditMain}
        </RoutineEditNavigator.Screen>
        <RoutineEditNavigator.Screen
          name="RoutineEditChecklist"
          component={RoutineEditChecklist}
          options={{
            gestureEnabled: false,
          }}
        />

        <RoutineEditNavigator.Screen
          name="RoutineEditOwnerTeam"
          component={RoutineEditOwnerTeam}
        />
        <RoutineEditNavigator.Screen
          name="RoutineEditScheduleDailyTaskImportance"
          component={RoutineEditScheduleDailyTaskImportance}
        />
        <RoutineEditNavigator.Screen
          name="RoutineEditChecklistOrderSections"
          component={RoutineEditChecklistOrderSections}
        />
        <RoutineEditNavigator.Screen
          name="RoutineEditChecklistSectionAddExistingTasks"
          component={RoutineEditChecklistSectionAddExistingTasks}
        />
        <RoutineEditNavigator.Screen
          name="RoutineEditChecklistSectionOrderTasks"
          component={RoutineEditChecklistSectionOrderTasks}
        />
        <RoutineEditNavigator.Screen
          name="RoutineEditChecklistSectionIssueTeam"
          component={RoutineEditChecklistSectionIssueTeam}
        />
        <RoutineEditNavigator.Screen
          name="RoutineEditChecklistTask"
          component={RoutineEditChecklistTask}
        />
        <RoutineEditNavigator.Screen
          name="RoutineEditFileSelect"
          component={RoutineEditFileSelect}
          options={{
            headerTitle: "",
          }}
        />
        <RoutineEditNavigator.Screen
          name="RoutineEditAllFiles"
          component={RoutineEditAllFiles}
        />
        <RoutineEditNavigator.Screen
          name="WorkspaceRoutineEditFileSelect"
          component={WorkspaceRoutineEditFileSelect}
        />
        <RoutineEditNavigator.Screen
          name="WorkspaceRoutineEditSectionSettings"
          component={WorkspaceRoutineEditSectionSettings}
        />
      </RoutineEditNavigator.Navigator>
    </RoutineEditStateProvider>
  );
}
